import { Box, Card, CardContent, Typography } from "@mui/material";
import Chart from 'react-apexcharts';
import { useEffect, useState } from "react";
import { formatAmount } from "../../../helper/formatAmount";
import { getChartDataList } from "../../../services/customer-services/customerDashboardService";

const DashboardChart = ({ clientId }) => {
    const [chartData, setChartData] = useState({ series: [], categories: [] });

    const getChartData = async () => {
        if (clientId) {
            try {
                const response = await getChartDataList(clientId);

                if (response?.isSuccess) {
                    setChartData({
                        series: response.data?.series || [],
                        categories: response.data?.categories || []
                    });
                } else {
                    setChartData({ series: [], categories: [] });
                }
            } catch (err) {
                setChartData({ series: [], categories: [] });
            }
        }
    }

    useEffect(() => {
        getChartData()
    }, [clientId]);


    // Safely calculate max value for chart
    const calculateMaxValue = () => {
        try {
            const allDataPoints = chartData?.series?.flatMap(series => series.data) || [];
            if (allDataPoints.length === 0) return 100000; // Default max if no data

            const maxValue = Math.max(...allDataPoints.filter(Number.isFinite));
            if (!Number.isFinite(maxValue)) return 100000; // Fallback if maxValue is not a number

            // Round up to nearest 100,000
            return Math.ceil(maxValue / 100000) * 100000;
        } catch (err) {
            return 100000; // Default fallback
        }
    };

    const roundedMax = calculateMaxValue();
    const tickAmount = 7;

    const options = {
        chart: {
            type: 'bar',
            height: 350,
            stacked: false,
            toolbar: {
                show: false
            },
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                borderRadius: 16,
                borderRadiusApplication: 'end',
            },
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            show: true,
            width: 2,
            colors: ['transparent']
        },
        xaxis: {
            categories: chartData.categories || [],
            labels: {
                rotate: -45, // Rotate labels 45 degrees
                style: {
                    fontSize: '10px',
                },
                hideOverlappingLabels: false, // Force all labels to show
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        grid: {
            show: true,
            borderColor: '#C9CBCD', // border color around the plot area
            strokeDashArray: 4,     // make it dashed
            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            }
        },
        yaxis: {
            labels: {
                formatter: function (value) {
                    return formatAmount(value);
                }
            },
            min: 0,
            max: roundedMax,
            tickAmount: tickAmount,
        },
        states: {
            hover: {
                filter: {
                    type: 'none', // disables hover effect
                }
            },
            active: {
                filter: {
                    type: 'none', // disables click/active effect
                }
            }
        },
        fill: {
            type: 'gradient',
            gradient: {
                shade: 'light',
                type: 'vertical',
                shadeIntensity: 0.5,
                gradientToColors: ['#826320', '#40330D'], // series 1 & 2
                inverseColors: false,
                opacityFrom: 1,
                opacityTo: 1,
                stops: [0, 100]
            },
        },
        colors: ['#F5D01C', '#000000'],
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            offsetY: -10
        },
        tooltip: {
            custom: function ({ series, dataPointIndex, w }) {
                const label = w?.globals?.labels?.[dataPointIndex];

                const seriesNames = w?.config?.series?.map(s => s.name);
                const values = series.map((s, i) => {
                    const val = s?.[dataPointIndex];
                    const colorBox = i === 0
                        ? `background: linear-gradient(to right, #F5D01C, #826320);`
                        : `background: linear-gradient(to bottom, #000000, #40330D);`;
                    return `
                    <div style="margin: 4px 0;">
                     <span style="
                        display: inline-block;
                        ${colorBox}
                        height: 12px;
                        width: 12px;
                        border-radius: 50%;
                        margin-right: 4px;
                        "></span>
                      <span style="font-weight: 500;">${seriesNames?.[i]}:</span> ${val?.toLocaleString() + ' ' + '€'}
                    </div>
                `;
                }).join('');

                return `
                <div style="padding: 8px 12px; border-radius: 6px;">
                    <strong style="font-size: 14px;">${label}</strong>
                    ${formatAmount(values)}
                </div>
                `;
            }
        },
        responsive: [
            {
                breakpoint: 1750,
                options: {
                    plotOptions: {
                        bar: {
                            borderRadius: 12,
                        }
                    }
                }
            },
            {
                breakpoint: 1500,
                options: {
                    plotOptions: {
                        bar: {
                            borderRadius: 10,
                        }
                    },
                    chart: {
                        width: '100%'
                    },
                }
            },
            {
                breakpoint: 1200,
                options: {
                    plotOptions: {
                        bar: {
                            borderRadius: 8,
                        }
                    },
                    chart: {
                        width: 800
                    },
                    legend: {
                        position: 'bottom'
                    }
                }
            },
            {
                breakpoint: 1000,
                options: {
                    chart: {
                        width: 900
                    },
                    legend: {
                        position: 'bottom'
                    }
                }
            }
        ]
    };

    return (
        <Box sx={{ paddingTop: "1.5rem" }}>
            <Card variant="outlined" sx={{ background: '#fff', padding: "10px 0", borderRadius: "10px", }}>
                <CardContent sx={{ background: "#fff" }}>
                    {chartData?.series?.length > 0 ? (
                        <Box sx={{
                            overflowX: 'auto',
                            width: "100%",
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            },
                            '-ms-overflow-style': 'none'
                        }}>
                            <Chart
                                options={options}
                                series={chartData?.series ?? []}
                                type="bar"
                                height={350}
                            />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                height: 350,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontFamily: 'Poppins',
                            }}
                        >
                            <Typography variant="h6">No chart data available.</Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    )
}

export default DashboardChart;